import React, { useMemo } from 'react';
import propTypes from 'prop-types';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import Button from 'tailwind-components/Button/Button';
import { cn } from 'lib/utils';

/**
 * Renders one "Coverage gap" recommendation card.
 *
 * Item shape (flattened from AgencyStateLeaderMissingAppointmentsResult):
 *   { agentId, agentName, state, policyCount, carrierId, carrierName }
 */

const US_STATE_ABBR = {
  Alabama: 'AL',
  Alaska: 'AK',
  Arizona: 'AZ',
  Arkansas: 'AR',
  California: 'CA',
  Colorado: 'CO',
  Connecticut: 'CT',
  Delaware: 'DE',
  Florida: 'FL',
  Georgia: 'GA',
  Hawaii: 'HI',
  Idaho: 'ID',
  Illinois: 'IL',
  Indiana: 'IN',
  Iowa: 'IA',
  Kansas: 'KS',
  Kentucky: 'KY',
  Louisiana: 'LA',
  Maine: 'ME',
  Maryland: 'MD',
  Massachusetts: 'MA',
  Michigan: 'MI',
  Minnesota: 'MN',
  Mississippi: 'MS',
  Missouri: 'MO',
  Montana: 'MT',
  Nebraska: 'NE',
  Nevada: 'NV',
  'New Hampshire': 'NH',
  'New Jersey': 'NJ',
  'New Mexico': 'NM',
  'New York': 'NY',
  'North Carolina': 'NC',
  'North Dakota': 'ND',
  Ohio: 'OH',
  Oklahoma: 'OK',
  Oregon: 'OR',
  Pennsylvania: 'PA',
  'Rhode Island': 'RI',
  'South Carolina': 'SC',
  'South Dakota': 'SD',
  Tennessee: 'TN',
  Texas: 'TX',
  Utah: 'UT',
  Vermont: 'VT',
  Virginia: 'VA',
  Washington: 'WA',
  'West Virginia': 'WV',
  Wisconsin: 'WI',
  Wyoming: 'WY',
  'District of Columbia': 'DC'
};

function getFirstName(fullName) {
  const trimmed = String(fullName || '').trim();
  if (!trimmed) return '';
  return trimmed.split(/\s+/)[0];
}

function getStateAbbr(state) {
  if (!state) return '';
  if (state.length === 2) return state.toUpperCase();
  return US_STATE_ABBR[state] || state;
}

// Carriers we explicitly call out as "top-selling" in the description copy.
// Anything outside this list keeps a more neutral wording so we don't overstate
// market position for niche carriers.
const TOP_SELLING_CARRIERS = [
  'anthem',
  'ambetter',
  'aetna',
  'bcbs',
  'cigna',
  'oscar',
  'united health care',
  'molina'
];

function isTopSellingCarrier(name) {
  if (!name) return false;
  const normalized = String(name).trim().toLowerCase();
  if (!normalized) return false;
  return TOP_SELLING_CARRIERS.includes(normalized);
}

function CoverageGapCard({
  gap,
  className,
  onNext,
  onPrev,
  onSuggest,
  suggestLoading = false,
  position,
  total
}) {
  const hasMultiple = Number.isFinite(Number(total)) && Number(total) > 1;
  const showCounter = Number.isFinite(Number(position)) && hasMultiple;
  const carrierName = gap?.carrierName || 'this carrier';
  const agentName = gap?.agentName || 'Unknown agent';
  const state = gap?.state || '';
  const stateAbbr = getStateAbbr(state);
  const policyCount = Number.isFinite(Number(gap?.policyCount))
    ? Number(gap.policyCount)
    : 0;
  const firstName = getFirstName(agentName);

  const description = useMemo(() => {
    const subject = firstName || 'This agent';
    const clientsLabel = policyCount === 1 ? 'client' : 'clients';
    const stateClause = state ? ` in ${state}` : '';
    const topSellingClause = isTopSellingCarrier(carrierName)
      ? ', which is a top-selling carrier in that market'
      : '';
    return (
      `${subject} has ${policyCount} active ${clientsLabel}${stateClause} ` +
      `but is not appointed with ${carrierName}${topSellingClause}. ` +
      'Getting appointed could expand their options when ' +
      're-enrolling these clients.'
    );
  }, [firstName, policyCount, state, carrierName]);

  const titleSuffix = state ? ` — ${state}` : '';
  const subtitleParts = [agentName, state].filter(Boolean);

  const clientsMetaLabel = policyCount === 1 ? 'client' : 'clients';
  const clientsMetaPrefix = stateAbbr
    ? `${stateAbbr} ${clientsMetaLabel}`
    : clientsMetaLabel;

  return (
    <article
      className={cn(
        'flex w-full min-w-0 flex-col gap-3 overflow-hidden rounded-[10px]',
        'border border-grey-lighter bg-white p-4 shadow-[0_2px_6px_rgba(0,0,0,0.06)]',
        className
      )}
    >
      <div className='flex items-center justify-between gap-2'>
        <span
          className={cn(
            'inline-flex w-fit items-center rounded-full px-2.5 py-1',
            'text-[10px] font-bold tracking-[0.08em] uppercase',
            'bg-[#ede9fe] text-[#6d28d9]'
          )}
        >
          Coverage gap
        </span>
        <Button
          variant='primary'
          size='xsmall'
          rounded='small'
          text='Suggest to agent'
          onClick={onSuggest}
          isLoading={suggestLoading}
          isDisabled={!onSuggest || suggestLoading}
          className='shrink-0'
        />
      </div>

      <div className='flex min-w-0 flex-col gap-0.5'>
        <h4 className='m-0 text-base leading-snug font-semibold text-primary-darkened'>
          {carrierName}
          {titleSuffix}
        </h4>
        {subtitleParts.length > 0 && (
          <p className='m-0 text-sm font-medium text-primary-emphasis-light'>
            {subtitleParts.join(' · ')}
          </p>
        )}
        {gap?.agencyName && (
          <p className='m-0 text-xs text-grey'>{gap.agencyName}</p>
        )}
      </div>

      <p className='m-0 text-sm leading-snug text-grey-dark'>{description}</p>

      <span className='text-xs text-grey'>
        <span className='font-semibold text-grey-dark'>
          {clientsMetaPrefix}:
        </span>{' '}
        {policyCount} active
      </span>

      {hasMultiple && (
        <div className='flex items-center justify-between gap-2 border-t border-grey-lighter pt-3'>
          <Button
            variant='default'
            outlined
            size='xsmall'
            rounded='small'
            icon={<ChevronLeft />}
            text='Previous'
            onClick={onPrev}
            isDisabled={!onPrev}
          />
          {showCounter && (
            <span className='text-[11px] font-medium text-grey'>
              {position} of {total}
            </span>
          )}
          <Button
            variant='default'
            outlined
            size='xsmall'
            rounded='small'
            text='Next'
            icon={<ChevronRight />}
            iconPosition='right'
            onClick={onNext}
            isDisabled={!onNext}
          />
        </div>
      )}
    </article>
  );
}

CoverageGapCard.propTypes = {
  gap: propTypes.shape({
    agentId: propTypes.string,
    agentName: propTypes.string,
    state: propTypes.string,
    policyCount: propTypes.number,
    carrierId: propTypes.string,
    carrierName: propTypes.string,
    agencyName: propTypes.string
  }).isRequired,
  className: propTypes.string,
  onNext: propTypes.func,
  onPrev: propTypes.func,
  onSuggest: propTypes.func,
  suggestLoading: propTypes.bool,
  position: propTypes.number,
  total: propTypes.number
};

export default CoverageGapCard;
